/* import __COLOCATED_TEMPLATE__ from './sq-dropdown.hbs'; */
/**
`sq-dropdown` consists of a trigger and a [popover component](/components/glass/sq-popover.html) (all `sq-popover` styles, like `popover-list`,
etc. are usable here). When the trigger is clicked, it will toggle the popover. `sq-dropdown`
yields the trigger and popover components to the block preconfigured with basic styling and behavior. You can
customize them with additional properties such as tagName and class.

By default, the popover does not close when clicked. To close the popover, either click the trigger or outside
of the popover. If the popover should close when clicked, call the action `dropdown.close` from the popover.

```example
@title Basic Example

@hbs
{{#sq-dropdown as |dropdown|}}
  {{#dropdown.trigger class="button caret-down"}}
    Options
  {{/dropdown.trigger}}
  {{#dropdown.popover class="popover--fly-down"}}
    <div class="popover-list__item hyperlink">Option 1</div>
    <div class="popover-list__item hyperlink">Option 2</div>
    <div class="popover-list__item hyperlink">Option 3</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

```example
@title Grouped Items

@hbs
{{#sq-dropdown as |dropdown|}}
  {{#dropdown.trigger class="button caret-down"}}
    Menu
  {{/dropdown.trigger}}
  {{#dropdown.popover class="popover-list popover-list--with-headers popover--fly-down"}}
    <div class="popover-list__header">Fruits</div>
    <div class="popover-list__item popover-list__item--with-headers">Apple</div>
    <div class="popover-list__item popover-list__item--with-headers">Banana</div>
    <div class="popover-list__header">Vegetables</div>
    <div class="popover-list__item popover-list__item--with-headers">Potato</div>
    <div class="popover-list__item popover-list__item--with-headers">Spinach</div>
    <div class="popover-list__header">Desserts</div>
    <div class="popover-list__item popover-list__item--with-headers">Pie</div>
    <div class="popover-list__item popover-list__item--with-headers">Cake</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

```example
@title Custom Trigger with Common Text Definition

@hbs
{{#sq-dropdown as |dropdown|}}
  {{#dropdown.trigger class="dropdown__trigger--text-only caret-down"}}
    Options
  {{/dropdown.trigger}}
  {{#dropdown.popover class="popover--fly-down"}}
  <div class="popover-list__item hyperlink">Option 1</div>
  <div class="popover-list__item hyperlink">Option 2</div>
  <div class="popover-list__item hyperlink">Option 3</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

```example
@title Using an Icon as Trigger

@hbs
{{#sq-dropdown  as |dropdown|}}
  {{dropdown.trigger class="icon-help"}}
  {{#dropdown.popover class="popover--fly-down"}}
    <div class="popover-list__item hyperlink" {{action dropdown.close}}>Option (and if you click me, I close)</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

```example
@title Using a Component as Trigger

@hbs
{{#sq-dropdown class="item-list-more-action-dropdown" as |dropdown|}}
  {{#dropdown.trigger}}
    <MoreActionsMenuTrigger />
  {{/dropdown.trigger}}
  {{#dropdown.popover class="popover-list popover--fly-down"}}
    <div class="popover-list__item">this is a popover</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

```example
@title Using the Dropdown in a Modal
@description This example sets sq-popover's constrainInParent to false to allow the popover portion of the dropdown to
show on top of the modal.

@controller-properties
{
  showModal: false,
  actions: {
    close() {
      this.set('showModal', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Standard Modal" show=showModal}}
  <div class="modal-layout">
    {{#dialog-modal title="Standard Modal" as |modal|}}
      {{#modal.body}}
        {{#sq-dropdown as |dropdown|}}
          {{#dropdown.trigger class="button caret-down"}}
            sq-popover constrainInParent is false
          {{/dropdown.trigger}}
          {{#dropdown.popover constrainInParent=false placement="bottom-start"}}
            <div class="popover-list__item hyperlink">Option 1</div>
            <div class="popover-list__item hyperlink">Option 2</div>
            <div class="popover-list__item hyperlink">Option 3</div>
          {{/dropdown.popover}}
        {{/sq-dropdown}}
      {{/modal.body}}
      {{#modal.footer as |footer|}}
        {{footer.button-secondary label="Cancel" click=(action "close")}}
        {{footer.button-secondary label="Delete" class="button--destructive-secondary" click=(action "close")}}
        {{footer.button-primary label="Ok" click=(action "close")}}
      {{/modal.footer}}
    {{/dialog-modal}}
  </div>
{{/toggle-code-example}}
```

```example
@title Dropdown Without Trigger Contextual Component
@description The popover can be toggled with the `dropdown.toggle` action.
@hbs
{{#sq-dropdown as |dropdown|}}
  <button class="button" {{action dropdown.toggle}}>Button</button>
  {{#dropdown.popover class="popover--fly-up"}}
    <div class="popover-list__item">Popover Item</div>
  {{/dropdown.popover}}
{{/sq-dropdown}}
```

### Parameters
@param {boolean} [isDisabled=false] When true, the dropdown is disabled and the trigger cannot be clicked.
@param {action} [onOpen] This action is sent when the dropdown is opened. Can be used to track Eventstream2 events.
@param {action} [onClose] This action is sent when the dropdown is closed.
@param {boolean} [closePopoverOnClick] When true, immediately closes popover when any part of it is clicked
@param {action} [enterPressed] if popover contains a list, and want to do something (select) on enterPressed, send the action here (passed through to `list-keyboard-event-handler`).
@param {boolean} [openByDefault] renders the dropdown with the popover opened by default
@param {list} [listItems] if popover contains a list, send the list of items to the keyboard handler to allow enterPressed to get the actual item sent to it instead of just a dom item.
*/

import { next, run } from '@ember/runloop';
import $ from 'jquery';
import Component from '@ember/component';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['dropdown'],
  classNameBindings: ['isActive:dropdown-is-active'],
  isDisabled: false,
  didInsertElement(...args) {
    this._super(...args);
    if (this.openByDefault) {
      this.send('open');
    }
  },
  willDestroyElement(...args) {
    this._super(...args);
    this.unbindApplicationClick();
  },
  bindApplicationClick() {
    // eslint-disable-next-line ember/no-jquery
    $(window).on(`click.${this.elementId} touchend.${this.elementId}`, (...args) => {
      run(() => this.applicationClick(...args));
    });
  },
  unbindApplicationClick() {
    $(window).off(`.${this.elementId}`); // eslint-disable-line ember/no-jquery
  },
  applicationClick(e) {
    if (this.get('isDestroyed') || this.get('isDestroying')) {
      return;
    }
    const inPopover = $(this.element).find(e.target).length; // eslint-disable-line ember/no-jquery
    if (!inPopover) {
      this.send('close');
    }
  },
  actions: {
    toggleActive() {
      if (this.get('isActive')) {
        this.send('close');
      } else {
        this.send('open');
      }
    },
    open() {
      if (this.onOpen) {
        this.onOpen();
      }
      this.set('isActive', true);
      next(() => this.bindApplicationClick());
    },
    close() {
      this.set('isActive', false);
      if (this.onClose) {
        this.onClose();
      }
      this.unbindApplicationClick();
    }
  }
});